// customerPricingService.js — per-STORE special pricing (Brian, 2026-08-24).
//
// One doc per customer RECORD in `customerPricing/{recordId}`:
//   items: { [sku]: { price, endsAt (Timestamp|millis|null), active (bool) } }
//   updatedAt / updatedBy — audit trail
//
// Semantics (settled with Brian):
// - STORE price WINS over an active global promo (he'll lower it by hand if
//   a public promo ever undercuts a store deal).
// - Staff cart deal-price overrides still beat everything (explicit intent).
// - `active: false` = toggled off but kept for easy re-enable; endsAt in the
//   past = auto-expired (computed live, no cron — same pattern as promos).
// - Authoring is ADMIN-only (rules enforce), with a confirm popup in the UI.
// - The ORDER TRANSACTION is the price authority: it re-reads this doc live
//   at submit, so stale carts/toggles can't leak a dead special price.
import { db } from "../config/firebase";
import { doc, getDoc, setDoc, serverTimestamp, deleteField } from "firebase/firestore";

function toMillis(v) {
  if (v == null) return null;
  if (typeof v.toMillis === "function") return v.toMillis();
  const t = new Date(v).getTime();
  return Number.isNaN(t) ? null : t;
}

/** Is this pricing entry currently in force? */
export function isSpecialActive(entry, now = Date.now()) {
  if (!entry || entry.active === false) return false;
  if (!(entry.price > 0)) return false;
  const ends = toMillis(entry.endsAt);
  return ends == null || ends >= now;
}

/** Active special price for a SKU from a pricing doc's data (null if none). */
export function specialPriceFor(pricingData, sku, now = Date.now()) {
  const entry = pricingData?.items?.[String(sku)];
  return isSpecialActive(entry, now) ? entry.price : null;
}

// ---- per-record session cache (5-min TTL, same policy as dataCache) ----
const TTL_MS = 5 * 60 * 1000;
const cache = new Map(); // recordId -> { at, data }

/** The store's pricing doc data ({items:{}} when none). Cached per tab. */
export async function getCustomerPricing(recordId, force = false) {
  if (!recordId) return { items: {} };
  const hit = cache.get(recordId);
  if (!force && hit && Date.now() - hit.at < TTL_MS) return hit.data;
  const snap = await getDoc(doc(db, "customerPricing", recordId));
  const data = snap.exists() ? snap.data() : { items: {} };
  cache.set(recordId, { at: Date.now(), data });
  return data;
}

export function invalidateCustomerPricing(recordId) {
  if (recordId) cache.delete(recordId);
  else cache.clear();
}

/** Admin: set/replace one SKU's entry. entry = { price, endsAt, active }. */
export async function saveSpecialPrice(recordId, sku, entry, adminUid) {
  await setDoc(doc(db, "customerPricing", recordId), {
    items: { [String(sku)]: entry },
    updatedAt: serverTimestamp(),
    updatedBy: adminUid
  }, { merge: true });
  invalidateCustomerPricing(recordId);
}

/** Admin: remove one SKU entirely from the store's list. */
export async function removeSpecialPrice(recordId, sku, adminUid) {
  await setDoc(doc(db, "customerPricing", recordId), {
    items: { [String(sku)]: deleteField() },
    updatedAt: serverTimestamp(),
    updatedBy: adminUid
  }, { merge: true });
  invalidateCustomerPricing(recordId);
}
